/**
 * @voltxampere/engine — Diode AC small-signal stamp (Sprint 22 D2)
 *
 * Linearised around the DC operating point. The diode admittance
 * comprises a real conductance and an imaginary capacitance branch:
 *
 *     I_d(V_d) = I_s · (exp(V_d / (n · Vt)) - 1)
 *     g_d = dI_d/dV_d = (I_s · exp(V_d / (n · Vt))) / (n · Vt)
 *
 *  Sprint 22 D2 adds the junction capacitance term:
 *
 *     C_depletion(V_d) = Cj0 / (1 - V_d / Vj)^M       (V_d < FC·Vj)
 *                     ≈ linearised knee past FC·Vj  (SPICE3 form)
 *     C_diffusion(V_d) = g_d · Tt                     (forward bias)
 *     C_total          = C_depletion + C_diffusion
 *
 *  AC admittance becomes Y_d(jω) = g_d + jω·C_total at the four
 *  cells of the diode anode/cathode terminals:
 *
 *    Y[a,a] += g_d + jωC,   Y[c,c] += g_d + jωC,
 *    Y[a,c] -= g_d + jωC,   Y[c,a] -= g_d + jωC.
 *
 *  Defaults are Cj0 = Tt = 0 — Sprint 5/6/7 / 20 fixtures stay
 *  exactly zero-diff (no AC change versus the Sprint 20 stamp).
 *  A fixture that opts into junction caps gets the standard
 *  hybrid-π high-frequency rolloff.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { StampParameterError } from "@voltxampere/shared";
import { getNumericParam, getTerminalNetId } from "../terminal-utils.js";
const MAX_EXP_ARG = 80;
function readOptionalNumber(params, key, fallback, predicate) {
    const raw = params[key];
    if (typeof raw !== "number" || !Number.isFinite(raw))
        return fallback;
    return predicate(raw) ? raw : fallback;
}
function readDiodeCapParams(params) {
    return {
        Cj0: readOptionalNumber(params, "Cj0", 0, (v) => v >= 0),
        Vj: readOptionalNumber(params, "Vj", 1, (v) => v > 0),
        M: readOptionalNumber(params, "M", 0.5, (v) => v > 0),
        Tt: readOptionalNumber(params, "Tt", 0, (v) => v >= 0),
        FC: readOptionalNumber(params, "FC", 0.5, (v) => v >= 0 && v < 1),
    };
}
/**
 * Depletion-region capacitance with the SPICE3 forward-bias
 * linearisation past `FC·Vj`. The reverse-bias formula is
 * `Cj0 / (1 - Vd/Vj)^M`; for Vd >= FC·Vj we use the linear
 * extrapolation
 *
 *   C_dep = (Cj0 / (1 - FC)^(1+M)) · (1 - FC·(1+M) + M·Vd/Vj)
 *
 * Pure function — exported for tests.
 */
export function diodeDepletionCap(Vd, p) {
    if (p.Cj0 === 0)
        return 0;
    const knee = p.FC * p.Vj;
    if (Vd < knee) {
        const ratio = 1 - Vd / p.Vj;
        if (ratio <= 0)
            return 0; // numerical guard; caller already handled FC linearisation
        return p.Cj0 / Math.pow(ratio, p.M);
    }
    // Linearised continuation past the knee.
    const baseDenom = Math.pow(1 - p.FC, 1 + p.M);
    return (p.Cj0 / baseDenom) * (1 - p.FC * (1 + p.M) + (p.M * Vd) / p.Vj);
}
export const diodeACStamp = (ctx, component) => {
    const Is = getNumericParam(component, "saturationCurrent", 1e-14);
    const N = getNumericParam(component, "ideality", 1);
    if (!(Is > 0) || !(N > 0)) {
        throw new StampParameterError(`diodeACStamp: component "${component.id}" has non-positive Is or N (got Is=${String(Is)}, N=${String(N)})`, {
            meta: {
                stampType: "diode",
                parameter: "Is/N",
                received: { Is, N },
                expected: "both > 0",
                componentId: component.id,
            },
        });
    }
    const aNet = getTerminalNetId(component, "anode");
    const cNet = getTerminalNetId(component, "cathode");
    const a = ctx.nodeIndex(aNet);
    const c = ctx.nodeIndex(cNet);
    const Va = ctx.dcNodeVoltages.get(aNet) ?? 0;
    const Vc = ctx.dcNodeVoltages.get(cNet) ?? 0;
    const Vd = Va - Vc;
    // kT/q at the analysis temperature.
    const Vt = thermalVoltage(ctx.temperature);
    const arg = Math.min(Vd / (N * Vt), MAX_EXP_ARG);
    const expTerm = Math.exp(arg);
    // Standard small-signal slope: g_d = Is·exp(Vd/(n·Vt)) / (n·Vt).
    const gd = (Is * expTerm) / (N * Vt);
    // Sprint 22 D2 — junction caps (zero-default).
    const caps = readDiodeCapParams(component.params);
    const Cdep = diodeDepletionCap(Vd, caps);
    const Cdiff = gd * caps.Tt;
    const Ctot = Cdep + Cdiff;
    const susceptance = ctx.omega * Ctot; // j·ωC
    ctx.addY(a, a, gd, susceptance);
    ctx.addY(c, c, gd, susceptance);
    ctx.addY(a, c, -gd, -susceptance);
    ctx.addY(c, a, -gd, -susceptance);
};
const BOLTZMANN_K = 1.380649e-23;
const ELEMENTARY_CHARGE_Q = 1.602176634e-19;
function thermalVoltage(temperatureK) {
    return (BOLTZMANN_K * temperatureK) / ELEMENTARY_CHARGE_Q;
}
//# sourceMappingURL=diode.js.map